import React, { useState, useRef, useEffect } from 'react';
import { X, Pencil, Type, Upload, Check } from 'lucide-react';
import { computeInitials } from '../utils/initials';

const SIGNATURE_FONTS = [
  { name: 'Classic', family: "'Dancing Script', cursive", weight: 700 },
  { name: 'Elegant', family: "'Great Vibes', cursive", weight: 400 },
  { name: 'Flowing', family: "'Sacramento', cursive", weight: 400 },
  { name: 'Casual', family: "'Caveat', cursive", weight: 600 },
  { name: 'Smooth', family: "'Pacifico', cursive", weight: 400 },
];

const SignatureModal = ({ isOpen, onClose, onSave, fieldId, isInitials = false, assignedSignatureFieldIds = [], signerName = '', fieldStyle = null, fieldWidth = null, fieldHeight = null }) => {
  const [mode, setMode] = useState('type');
  const [typedText, setTypedText] = useState('');
  const [selectedFont, setSelectedFont] = useState(0);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);

  // Only show "apply to all" if there are multiple assigned fields of the same type
  const otherFields = assignedSignatureFieldIds.filter(id => id !== fieldId);
  const showApplyToAll = otherFields.length > 0;

  useEffect(() => {
    if (isOpen) {
      // Reset state every time the modal opens so the signer can't accidentally
      // carry text from a previous field (e.g., typing "John Doe" for signature
      // and having it pre-filled into the next Initials modal).
      setMode('type');
      // Pre-fill for DocuSign-style UX:
      //   • Signature field → full name (e.g. "Rohit Singh")
      //   • Initials field  → derived initials (e.g. "RS")
      // In both cases the signer can overwrite before saving.
      const nameToSeed = (signerName || '').trim();
      setTypedText(nameToSeed ? (isInitials ? computeInitials(nameToSeed) : nameToSeed) : '');
      setSelectedFont(0);
      setHasDrawn(false);
      // Phase 66: Safety-first default — the "Apply to all my assigned fields"
      // checkbox must start UNCHECKED. Users must explicitly opt-in to bulk
      // apply; the previous "default ON" behavior was unsafe when counts were
      // inflated. The checkbox still renders whenever >1 owned field exists.
      setApplyToAll(false);
      if (mode === 'draw') {
        initializeCanvas();
      }
    }
    // eslint-disable-next-line
  }, [isOpen]);

  // Re-initialize the canvas whenever mode switches to 'draw' (after the canvas
  // DOM node remounts). Separate from the open-reset effect above.
  useEffect(() => {
    if (isOpen && mode === 'draw') initializeCanvas();
    // eslint-disable-next-line
  }, [mode, isOpen]);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  // Map a pointer event's viewport coords to the canvas's internal pixel
  // space. The canvas backing store is a fixed 600x200, but it renders at
  // `w-full` (any CSS width), so the displayed size rarely matches. Without
  // scaling, strokes drift from the cursor/finger on every width != 600px —
  // which is every mobile/tablet screen. Scale by the ratio of backing-store
  // size to the rendered rect so ink lands exactly under the pointer.
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // Pointer Events unify mouse, touch and stylus in a single codepath
  // (supported on Chrome Android, Safari iOS 13+, Samsung Internet, Edge
  // Mobile). preventDefault + touch-action:none on the canvas stop the page
  // from scrolling/zooming while the finger draws.
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
    // Capture the pointer so move/up keep firing even if the finger slides
    // outside the canvas bounds mid-stroke.
    if (e.pointerId != null && canvas.setPointerCapture) {
      try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* unsupported */ }
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e && e.pointerId != null && canvasRef.current?.releasePointerCapture) {
      try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (_) { /* no-op */ }
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // Trim transparent whitespace from a canvas so the stored image tightly
  // wraps the ink. Without trimming, object-contain scales the whole canvas
  // (including empty margins) to fit the field box, making the actual content
  // appear much smaller than the configured font size.
  const trimCanvasToContent = (canvas, padding = 6) => {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);
    let top = height, bottom = 0, left = width, right = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 10) {
          if (y < top) top = y;
          if (y > bottom) bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }
    if (top > bottom || left > right) return canvas.toDataURL('image/png');
    top = Math.max(0, top - padding);
    bottom = Math.min(height - 1, bottom + padding);
    left = Math.max(0, left - padding);
    right = Math.min(width - 1, right + padding);
    const tw = right - left + 1;
    const th = bottom - top + 1;
    const trimmed = document.createElement('canvas');
    trimmed.width = tw;
    trimmed.height = th;
    trimmed.getContext('2d').drawImage(canvas, left, top, tw, th, 0, 0, tw, th);
    return trimmed.toDataURL('image/png');
  };

  const generateTypedSignatureImage = (text, fontIndex) => {
    const font = SIGNATURE_FONTS[fontIndex];

    // Configured (authored) font size from Visual Builder — px units.
    // Fallback defaults match the placeholder text so signed output
    // visually matches the unsigned preview.
    const authoredFontSize = (() => {
      if (fieldStyle?.fontSize) {
        const n = Number(fieldStyle.fontSize.toString().replace('px', ''));
        if (Number.isFinite(n) && n > 0) return n;
      }
      return isInitials ? 16 : 18;
    })();

    // Sharpness multiplier — canvas is rasterised at 3× pixel density
    // so the final PDF stays crisp on print.
    const DPI = 3;
    const glyphH = authoredFontSize * DPI;

    // ── TIGHT canvas: text fills the canvas height edge-to-edge ──
    // The signer UI and backend PDF render this image at exactly
    // `authoredFontSize` CSS / PDF pixels tall. To make the rendered
    // glyphs appear at exactly `authoredFontSize` (matching the
    // placeholder text), the text MUST fill the canvas height —
    // otherwise the inner glyphs scale down with the surrounding
    // padding.  Use the font's actual bounding box metrics for an
    // accurate fit, with a small fallback when metrics aren't
    // available.
    const measureCanvas = document.createElement('canvas');
    const mctx = measureCanvas.getContext('2d');
    mctx.font = `${font.weight} ${glyphH}px ${font.family}`;
    const m = mctx.measureText(text);

    // Real ink height when metrics are available; else assume tight
    // glyph height matches the font size.
    const ascent = Number.isFinite(m.actualBoundingBoxAscent)
      ? Math.max(0, m.actualBoundingBoxAscent) : glyphH * 0.78;
    const descent = Number.isFinite(m.actualBoundingBoxDescent)
      ? Math.max(0, m.actualBoundingBoxDescent) : glyphH * 0.22;
    const inkHeight = Math.max(glyphH * 0.8, ascent + descent);
    const measuredW = Math.max(glyphH * 0.6, m.width);

    // Tiny side padding so glyphs don't touch the image edges, but
    // VERTICAL padding is zero — the canvas height equals the text
    // bounding box. This is what guarantees the rendered signature
    // matches the configured font size in CSS / PDF pixels.
    const padX = Math.max(2 * DPI, glyphH * 0.12);

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(measuredW + padX * 2);
    canvas.height = Math.round(inkHeight);
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.font = `${font.weight} ${glyphH}px ${font.family}`;
    ctx.fillStyle = (fieldStyle?.color) || '#1a1a2e';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Position baseline so ascenders fit at the top and descenders
    // just barely fit at the bottom of the canvas.
    ctx.fillText(text, canvas.width / 2, ascent);

    return canvas.toDataURL('image/png');
  };

  const removeWhiteBackground = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      // If pixel is near-white (R, G, B > 240), make it transparent
      if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) {
        data[i + 3] = 0;
      }
    }
    ctx.putImageData(imgData, 0, 0);
  };

  const handleSave = () => {
    let signatureData = null;
    if (mode === 'draw') {
      if (!hasDrawn) { alert('Please draw your signature first'); return; }
      const canvas = canvasRef.current;
      removeWhiteBackground(canvas);
      signatureData = trimCanvasToContent(canvas);
    } else if (mode === 'type') {
      if (!typedText.trim()) { alert('Please type your signature first'); return; }
      signatureData = generateTypedSignatureImage(typedText, selectedFont);
    }
    if (signatureData) {
      if (applyToAll && otherFields.length > 0) {
        // Apply to current field + all other assigned signature fields
        onSave(fieldId, signatureData, [fieldId, ...otherFields]);
      } else {
        onSave(fieldId, signatureData, null);
      }
      onClose();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        removeWhiteBackground(canvas);
        onSave(fieldId, canvas.toDataURL('image/png'));
        onClose();
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  // Rendered inside each mode's content stack rather than pinned above the
  // footer, so in Type mode it sits between the name input and the style
  // picker instead of below a long, scrollable list of styles.
  const applyToAllCheckbox = showApplyToAll ? (
    <label
      className="flex items-center gap-2.5 cursor-pointer select-none p-3 rounded-lg bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 transition-colors"
      data-testid="apply-to-all-checkbox"
    >
      <input
        type="checkbox"
        checked={applyToAll}
        onChange={(e) => setApplyToAll(e.target.checked)}
        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 flex-shrink-0"
      />
      <span className="text-sm text-indigo-800 font-medium">
        Apply this {isInitials ? 'initial' : 'signature'} to all my assigned {isInitials ? 'initial' : 'signature'} fields ({otherFields.length + 1} fields)
      </span>
    </label>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-y-auto" data-testid="signature-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            {isInitials ? 'Add Initials' : 'Add Signature'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="signature-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMode('draw')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mode === 'draw' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            data-testid="signature-mode-draw"
          >
            <Pencil className="h-4 w-4" />
            Draw
          </button>
          <button
            onClick={() => setMode('type')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mode === 'type' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            data-testid="signature-mode-type"
          >
            <Type className="h-4 w-4" />
            Type
          </button>
          <button
            onClick={() => setMode('upload')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mode === 'upload' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            data-testid="signature-mode-upload"
          >
            <Upload className="h-4 w-4" />
            Upload
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {mode === 'draw' && (
            <div className="space-y-4">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerCancel={stopDrawing}
                className="w-full border-2 border-gray-300 rounded cursor-crosshair bg-white touch-none select-none"
                style={{ touchAction: 'none' }}
                data-testid="signature-canvas"
              />
              <button onClick={clearCanvas} className="text-sm text-gray-600 hover:text-gray-800" data-testid="clear-canvas-btn">
                Clear
              </button>
              {applyToAllCheckbox}
            </div>
          )}

          {mode === 'type' && (
            <div className="space-y-4">
              <input
                type="text"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder={isInitials ? 'Type your initials' : 'Type your name'}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 text-lg"
                autoFocus
                data-testid="signature-type-input"
              />
              {applyToAllCheckbox}
              {typedText && (
                <>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Choose a style</p>
                  <div className="grid grid-cols-1 gap-2" data-testid="signature-suggestions">
                    {SIGNATURE_FONTS.map((font, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedFont(idx)}
                        className={`relative w-full h-16 border-2 rounded-lg flex items-center justify-center bg-white transition-all ${selectedFont === idx
                            ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50/50'
                            : 'border-gray-200 hover:border-gray-400'
                          }`}
                        data-testid={`signature-style-${idx}`}
                      >
                        <span
                          style={{
                            fontFamily: font.family,
                            fontWeight: font.weight,
                            fontSize: isInitials ? '28px' : '34px',
                            color: '#1a1a2e',
                          }}
                        >
                          {typedText}
                        </span>
                        {selectedFont === idx && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                        <span className="absolute bottom-0.5 right-2 text-[10px] text-gray-400">
                          {font.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {mode === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600 mb-4">Upload an image of your signature</p>
                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="signature-upload" />
                <label htmlFor="signature-upload" className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer" data-testid="signature-upload-btn">
                  Choose File
                </label>
              </div>
              {applyToAllCheckbox}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-200 flex-wrap">
          <button onClick={onClose} className="px-3 sm:px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg min-h-[40px]" data-testid="signature-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={mode === 'upload'}
            className="px-4 sm:px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 min-h-[40px]"
            data-testid="signature-save-btn"
          >
            {isInitials ? 'Add Initials' : 'Add Signature'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
